/**
 * Órdenes API Service
 * CRUD operations for orders, exams, profiles, reference values and quotes
 */

import { api } from "@/lib/api";
import type { PaginatedResponse, FetchParams } from "@/hooks/use-server-data-table";

// =============================================================================
// URLS
// =============================================================================

const ORDENES_URL = "/ordenes/";
const ORDEN_EXAMENES_URL = "/orden-examenes/";
const EXAMENES_TIPO_URL = "/examenes-tipo/";
const PERFILES_URL = "/perfiles/";
const VALORES_REF_URL = "/valores-referencia/";
const COTIZACIONES_URL = "/cotizaciones/";

// =============================================================================
// TYPES - ExamenTipo
// =============================================================================

export type AreaLaboratorio =
  | "HEMATOLOGIA"
  | "QUIMICA"
  | "SEROLOGIA"
  | "INMUNOLOGIA"
  | "URINALISIS"
  | "COAGULACION"
  | "MICROBIOLOGIA"
  | "PARASITOLOGIA"
  | "HORMONAS"
  | "MARCADORES"
  | "DROGAS"
  | "ESPECIAL";

export type TipoResultado = "NUMERICO" | "TEXTO" | "OPCIONES" | "FORMULA";

export interface ExamenTipo {
  id: number;
  codigo: string;
  nombre: string;
  nombre_corto: string;
  area: AreaLaboratorio;
  area_display: string;
  unidad: string;
  precio: number;
  tipo_resultado: TipoResultado;
  tipo_resultado_display: string;
  opciones_resultado: string[];
  requiere_ayuno: boolean;
  dias_resultado: number;
  instrucciones_paciente: string;
  notas_internas: string;
  // Campos preanalíticos (task 1.6)
  tipo_contenedor_default: number | null;
  tipo_contenedor_nombre: string | null;
  volumen_minimo_ml: string | null;
  condiciones_especiales: string;
  // Tiempo estimado de entrega (issue #99)
  dias_para_resultado: number | null;
  horas_para_resultado: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExamenTipoListItem {
  id: number;
  codigo: string;
  nombre: string;
  nombre_corto: string;
  area: AreaLaboratorio;
  area_display: string;
  unidad: string;
  precio: number;
  requiere_ayuno: boolean;
  is_active: boolean;
}

export interface ExamenTipoCreateData {
  codigo: string;
  nombre: string;
  nombre_corto?: string;
  area: AreaLaboratorio;
  unidad?: string;
  precio?: number;
  tipo_resultado?: TipoResultado;
  opciones_resultado?: string[];
  requiere_ayuno?: boolean;
  dias_resultado?: number;
  instrucciones_paciente?: string;
  notas_internas?: string;
  // Campos preanalíticos (task 1.6 / 11.8)
  tipo_contenedor_default?: number | null;
  volumen_minimo_ml?: number | null;
  condiciones_especiales?: string;
  // Tiempo estimado de entrega (issue #99)
  dias_para_resultado?: number | null;
  horas_para_resultado?: number | null;
}

// =============================================================================
// TYPES - PerfilExamen
// =============================================================================

export interface PerfilExamen {
  id: number;
  codigo: string;
  nombre: string;
  examenes: ExamenTipoListItem[];
  precio: number;
  precio_individual_total: number;
  ahorro: number;
  descripcion: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PerfilExamenListItem {
  id: number;
  codigo: string;
  nombre: string;
  precio: number;
  precio_individual_total: number;
  ahorro: number;
  num_examenes: number;
  examenes_nombres: string[];
  examenes_ids: number[];
  descripcion: string;
  is_active: boolean;
}

export interface PerfilExamenCreateData {
  codigo: string;
  nombre: string;
  examenes: number[];
  precio?: number;
  descripcion?: string;
}

// =============================================================================
// TYPES - ValorReferencia
// =============================================================================

export interface ValorReferencia {
  id: number;
  examen_tipo: number;
  examen_tipo_nombre: string;
  sexo: "M" | "F" | "A";
  edad_min_dias: number;
  edad_max_dias: number;
  rango_edad_str: string;
  valor_min: number | null;
  valor_max: number | null;
  valor_texto: string;
  rango_str: string;
  unidad: string;
  descripcion: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ValorReferenciaCreateData {
  examen_tipo: number;
  sexo?: "M" | "F" | "A";
  edad_min_dias?: number;
  edad_max_dias?: number;
  valor_min?: number | null;
  valor_max?: number | null;
  valor_texto?: string;
  unidad?: string;
  descripcion?: string;
}

// =============================================================================
// TYPES - Orden
// =============================================================================

export type EstadoOrden = "EN_EXTRACCION" | "PENDIENTE" | "CON_RESULTADO" | "FINALIZADA" | "BAJA";
export type PrioridadOrden = "NORMAL" | "URGENTE" | "STAT";
export type TipoEntrega = "PRESENCIAL" | "EMAIL" | "WHATSAPP" | "PORTAL";
export type TipoServicio = "AMBULATORIO" | "EMERGENCIA" | "HOSPITALIZACION" | "DOMICILIO";
export type EstadoOrdenExamen =
  | "PENDIENTE"
  | "EN_PROCESO"
  | "CON_RESULTADO"
  | "VALIDADO"
  | "CANCELADO";

export interface OrdenExamen {
  id: number;
  orden: number;
  examen_tipo: number;
  examen_tipo_nombre: string;
  examen_tipo_codigo: string;
  examen_tipo_area: string;
  perfil_origen: number | null;
  perfil_origen_nombre: string | null;
  estado: EstadoOrdenExamen;
  estado_display: string;
  precio: number;
  observaciones: string;
  is_active: boolean;
  created_at: string;
}

export interface OrdenExamenListItem {
  id: number;
  orden: number;
  orden_numero: string;
  orden_fecha: string;
  orden_estado: EstadoOrden;
  orden_estado_display: string;
  orden_prioridad: PrioridadOrden;
  orden_prioridad_display: string;
  orden_tipo_cobro: string | null;
  paciente_nombre: string;
  sucursal_nombre: string;
  examen_tipo: number;
  examen_tipo_nombre: string;
  examen_tipo_codigo: string;
  examen_tipo_area: AreaLaboratorio;
  examen_tipo_area_display: string;
  perfil_origen: number | null;
  perfil_origen_nombre: string | null;
  estado: EstadoOrdenExamen;
  estado_display: string;
  precio: number;
  is_active: boolean;
  created_at: string;
}

export interface Orden {
  id: number;
  numero: string;
  paciente: number;
  paciente_nombre: string;
  paciente_edad_texto: string | null;
  paciente_sexo_display: string | null;
  medico_remitente: number | null;
  medico_remitente_nombre: string | null;
  cliente: number | null;
  cliente_nombre: string | null;
  lista_precio: number | null;
  lista_precio_nombre: string | null;
  lista_precio_porcentaje: number | null;
  sucursal: number;
  sucursal_nombre: string;
  fecha: string;
  prioridad: PrioridadOrden;
  prioridad_display: string;
  estado: EstadoOrden;
  estado_display: string;
  tipo_entrega: TipoEntrega;
  tipo_entrega_display: string;
  tipo_servicio: TipoServicio;
  tipo_servicio_display: string;
  observaciones: string;
  descuento_porcentaje: number;
  descuento_lempiras: number;
  examenes: OrdenExamen[];
  total_examenes: number;
  examenes_pendientes: number;
  examenes_validados: number;
  todos_validados: boolean;
  pagada: boolean;
  pagos: PagoOrden[];
  total_pagado: string;
  saldo_pendiente: string;
  pago: PagoOrden | null;
  tipo_cobro: string | null;
  tipo_cobro_display: string;
  es_editable: boolean;
  es_cortesia: boolean;
  autorizado_por_cortesia: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type TipoPagoOrden = "ANTICIPO" | "ABONO" | "CANCELACION";

export interface PagoOrden {
  id: number;
  monto: string;
  monto_recibido: string;
  vuelto: string;
  metodo: number;
  metodo_nombre: string;
  metodo_tipo: string;
  referencia: string;
  fecha: string;
  tipo_pago: TipoPagoOrden;
  tipo_pago_display: string;
  is_active: boolean;
  created_at: string;
}

export interface PagoOrdenResult {
  pago: PagoOrden;
  vuelto: string;
}

export interface OrdenListItem {
  id: number;
  numero: string;
  paciente: number;
  paciente_nombre: string;
  cliente_nombre: string | null;
  cliente_rtn: string;
  sucursal_nombre: string;
  fecha: string;
  prioridad: PrioridadOrden;
  prioridad_display: string;
  estado: EstadoOrden;
  estado_display: string;
  tipo_servicio: TipoServicio;
  tipo_servicio_display: string;
  total_examenes: number;
  descuento_porcentaje: number;
  descuento_lempiras: number;
  total: string;
  pagada: boolean;
  tipo_cobro: string | null;
  tipo_cobro_display: string;
  total_pagado: string;
  saldo_pendiente: string;
  es_editable: boolean;
  es_cortesia: boolean;
  autorizado_por_cortesia: string;
  is_active: boolean;
  created_at: string;
}

export interface OrdenCreateData {
  paciente: number;
  sucursal: number;
  medico_remitente?: number | null;
  cliente?: number | null;
  lista_precio?: number | null;
  examenes_ids?: number[];
  perfiles_ids?: number[];
  prioridad?: PrioridadOrden;
  tipo_entrega?: TipoEntrega;
  tipo_servicio?: TipoServicio;
  observaciones?: string;
  descuento_porcentaje?: number;
  descuento_lempiras?: number;
}

export interface OrdenUpdateData {
  medico_remitente?: number | null;
  cliente?: number | null;
  lista_precio?: number | null;
  examenes_ids?: number[];
  perfiles_ids?: number[];
  prioridad?: PrioridadOrden;
  tipo_entrega?: TipoEntrega;
  tipo_servicio?: TipoServicio;
  observaciones?: string;
  descuento_porcentaje?: number;
  descuento_lempiras?: number;
}

// =============================================================================
// TYPES - Cotización
// =============================================================================

export type EstadoCotizacion = "VIGENTE" | "CONVERTIDA" | "VENCIDA" | "CANCELADA";

export interface CotizacionExamen {
  id: number;
  examen_tipo: number;
  examen_tipo_nombre: string;
  examen_tipo_codigo: string;
  precio: number;
}

export interface Cotizacion {
  id: number;
  numero: string;
  paciente: number | null;
  paciente_nombre: string;
  sucursal: number;
  sucursal_nombre: string;
  estado: EstadoCotizacion;
  estado_display: string;
  fecha_vigencia: string;
  total: number;
  examenes: CotizacionExamen[];
  esta_vigente: boolean;
  total_examenes: number;
  orden_generada: number | null;
  observaciones: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CotizacionListItem {
  id: number;
  numero: string;
  paciente: number | null;
  paciente_nombre: string;
  sucursal_nombre: string;
  estado: EstadoCotizacion;
  estado_display: string;
  fecha_vigencia: string;
  total: number;
  total_examenes: number;
  is_active: boolean;
  created_at: string;
}

export interface CotizacionCreateData {
  sucursal: number;
  paciente?: number | null;
  examenes_ids: number[];
  dias_vigencia?: number;
  observaciones?: string;
}

export interface CotizacionConvertirData {
  paciente?: number | null;
  medico_remitente?: number | null;
}

// =============================================================================
// API - ExamenTipo
// =============================================================================

export async function getExamenesTipo(
  params?: FetchParams
): Promise<PaginatedResponse<ExamenTipoListItem>> {
  const response = await api.get(EXAMENES_TIPO_URL, { params });
  return response.data;
}

export async function getExamenTipo(id: number): Promise<ExamenTipo> {
  const response = await api.get(`${EXAMENES_TIPO_URL}${id}/`);
  return response.data;
}

export async function createExamenTipo(data: ExamenTipoCreateData): Promise<ExamenTipo> {
  const response = await api.post(EXAMENES_TIPO_URL, data);
  return response.data;
}

export async function updateExamenTipo(
  id: number,
  data: Partial<ExamenTipoCreateData>
): Promise<ExamenTipo> {
  const response = await api.patch(`${EXAMENES_TIPO_URL}${id}/`, data);
  return response.data;
}

export async function deleteExamenTipo(id: number): Promise<void> {
  await api.delete(`${EXAMENES_TIPO_URL}${id}/`);
}

export async function deactivateExamenTipo(id: number): Promise<ExamenTipo> {
  const response = await api.post(`${EXAMENES_TIPO_URL}${id}/deactivate/`);
  return response.data;
}

export async function activateExamenTipo(id: number): Promise<ExamenTipo> {
  const response = await api.post(`${EXAMENES_TIPO_URL}${id}/activate/`);
  return response.data;
}

export async function buscarExamenesTipo(query: string, limit = 50): Promise<ExamenTipoListItem[]> {
  const response = await api.get(`${EXAMENES_TIPO_URL}buscar/`, { params: { q: query, limit } });
  return response.data;
}

// =============================================================================
// API - PerfilExamen
// =============================================================================

export async function getPerfiles(
  params?: FetchParams
): Promise<PaginatedResponse<PerfilExamenListItem>> {
  const response = await api.get(PERFILES_URL, { params });
  return response.data;
}

export async function getPerfil(id: number): Promise<PerfilExamen> {
  const response = await api.get(`${PERFILES_URL}${id}/`);
  return response.data;
}

export async function createPerfil(data: PerfilExamenCreateData): Promise<PerfilExamen> {
  const response = await api.post(PERFILES_URL, data);
  return response.data;
}

export async function updatePerfil(
  id: number,
  data: Partial<PerfilExamenCreateData>
): Promise<PerfilExamen> {
  const response = await api.patch(`${PERFILES_URL}${id}/`, data);
  return response.data;
}

export async function deletePerfil(id: number): Promise<void> {
  await api.delete(`${PERFILES_URL}${id}/`);
}

export async function deactivatePerfil(id: number): Promise<PerfilExamen> {
  const response = await api.post(`${PERFILES_URL}${id}/deactivate/`);
  return response.data;
}

export async function activatePerfil(id: number): Promise<PerfilExamen> {
  const response = await api.post(`${PERFILES_URL}${id}/activate/`);
  return response.data;
}

export async function buscarPerfiles(query: string, limit = 50): Promise<PerfilExamenListItem[]> {
  const response = await api.get(`${PERFILES_URL}buscar/`, {
    params: { q: query, limit },
  });
  return response.data.results;
}

// =============================================================================
// API - ValorReferencia
// =============================================================================

export async function getValoresReferencia(
  params?: FetchParams
): Promise<PaginatedResponse<ValorReferencia>> {
  const response = await api.get(VALORES_REF_URL, { params });
  return response.data;
}

export async function createValorReferencia(
  data: ValorReferenciaCreateData
): Promise<ValorReferencia> {
  const response = await api.post(VALORES_REF_URL, data);
  return response.data;
}

export async function updateValorReferencia(
  id: number,
  data: Partial<ValorReferenciaCreateData>
): Promise<ValorReferencia> {
  const response = await api.patch(`${VALORES_REF_URL}${id}/`, data);
  return response.data;
}

export async function deleteValorReferencia(id: number): Promise<void> {
  await api.delete(`${VALORES_REF_URL}${id}/`);
}

// =============================================================================
// API - Orden
// =============================================================================

export async function getOrdenes(params?: FetchParams): Promise<PaginatedResponse<OrdenListItem>> {
  const response = await api.get(ORDENES_URL, { params });
  return response.data;
}

export async function getOrden(id: number): Promise<Orden> {
  const response = await api.get(`${ORDENES_URL}${id}/`);
  return response.data;
}

export async function createOrden(data: OrdenCreateData): Promise<Orden> {
  const response = await api.post(ORDENES_URL, data);
  return response.data;
}

export async function updateOrden(id: number, data: OrdenUpdateData): Promise<Orden> {
  const response = await api.patch(`${ORDENES_URL}${id}/`, data);
  return response.data;
}

export async function deleteOrden(id: number): Promise<void> {
  await api.delete(`${ORDENES_URL}${id}/`);
}

export async function deactivateOrden(id: number): Promise<Orden> {
  const response = await api.post(`${ORDENES_URL}${id}/deactivate/`);
  return response.data;
}

export async function activateOrden(id: number): Promise<Orden> {
  const response = await api.post(`${ORDENES_URL}${id}/activate/`);
  return response.data;
}

export async function cambiarEstadoOrden(id: number, estado: EstadoOrden): Promise<Orden> {
  const response = await api.post(`${ORDENES_URL}${id}/cambiar-estado/`, { estado });
  return response.data;
}

export async function darDeBajaOrden(id: number, motivo: string): Promise<Orden> {
  const response = await api.post(`${ORDENES_URL}${id}/dar-de-baja/`, { motivo });
  return response.data;
}

export async function getOrdenExamenes(id: number): Promise<OrdenExamen[]> {
  const response = await api.get(`${ORDENES_URL}${id}/examenes/`);
  return response.data;
}

export async function getOrdenExamenesList(
  params?: FetchParams
): Promise<PaginatedResponse<OrdenExamenListItem>> {
  const response = await api.get(ORDEN_EXAMENES_URL, { params });
  return response.data;
}

export async function getReciboPdf(ordenId: number): Promise<Blob> {
  const response = await api.get(`/ordenes/${ordenId}/recibo/pdf/`, {
    responseType: "blob",
  });
  return response.data;
}

export interface PagarOrdenData {
  metodo: number;
  monto_recibido: number;
  tipo_pago?: TipoPagoOrden;
  referencia?: string;
}

export async function pagarOrden(id: number, data: PagarOrdenData): Promise<PagoOrdenResult> {
  const response = await api.post(`${ORDENES_URL}${id}/pagar/`, data);
  return response.data;
}

export async function getPagosOrden(id: number): Promise<PagoOrden[]> {
  const response = await api.get(`${ORDENES_URL}${id}/pagos/`);
  return response.data;
}

export async function anularPagoOrden(id: number): Promise<void> {
  await api.post(`${ORDENES_URL}${id}/anular-pago/`);
}

export async function anularPagoEspecifico(ordenId: number, pagoId: number): Promise<void> {
  await api.post(`${ORDENES_URL}${ordenId}/anular-pago/${pagoId}/`);
}

export async function marcarCortesia(id: number, autorizado_por: string): Promise<Orden> {
  const response = await api.post(`${ORDENES_URL}${id}/marcar-cortesia/`, { autorizado_por });
  return response.data;
}

// =============================================================================
// API - Cotización
// =============================================================================

export async function getCotizaciones(
  params?: FetchParams
): Promise<PaginatedResponse<CotizacionListItem>> {
  const response = await api.get(COTIZACIONES_URL, { params });
  return response.data;
}

export async function getCotizacion(id: number): Promise<Cotizacion> {
  const response = await api.get(`${COTIZACIONES_URL}${id}/`);
  return response.data;
}

export async function createCotizacion(data: CotizacionCreateData): Promise<Cotizacion> {
  const response = await api.post(COTIZACIONES_URL, data);
  return response.data;
}

export async function deleteCotizacion(id: number): Promise<void> {
  await api.delete(`${COTIZACIONES_URL}${id}/`);
}

export async function deactivateCotizacion(id: number): Promise<Cotizacion> {
  const response = await api.post(`${COTIZACIONES_URL}${id}/deactivate/`);
  return response.data;
}

export async function activateCotizacion(id: number): Promise<Cotizacion> {
  const response = await api.post(`${COTIZACIONES_URL}${id}/activate/`);
  return response.data;
}

export async function convertirCotizacion(
  id: number,
  data: CotizacionConvertirData
): Promise<Orden> {
  const response = await api.post(`${COTIZACIONES_URL}${id}/convertir/`, data);
  return response.data;
}
